import { Role } from '@/types';
import { IToken } from '@/types/token.type';
import { IUser } from '@/types/user.type';
import { EMedicalItemType, IApprovalRequest, IInsurance, IMedicalItem, IPatient, ITariff, IRMPractitioner } from '@/types/v2';
import { IRMFacility } from '@/services/rssb-mocks/facility';
import { IVoucher } from '@/services/rssb-mocks/vouchers';
import { atom } from 'recoil-next';

export const showInviteInsuranceState = atom({
   key: 'showInviteInsuranceState',
   default: false,
});

export const showAddHospitalState = atom({
   key: 'showAddHospitalState',
   default: false,
});

export const showAddPaymentState = atom({
   key: 'showAddPaymentState',
   default: false,
});

export const showAddMedicalPractitionerState = atom({
   key: 'showAddDoctorState',
   default: false,
});

export const showAddEmployeeState = atom({
   key: 'showAddEmployeeState',
   default: false,
});

export const showCreateEligibilityCheckState = atom({
   key: 'showCreateEligibilityCheckState',
   default: false,
});
export const showDeactivateInsuranceState = atom<{
   show: boolean;
   insuranceId: string;
   action: 'deactivate' | 'activate';
} | null>({
   key: 'showDeactivateState',
   default: null,
});

export const showCreateNewMemberState = atom({
   key: 'showCreateNewMemberState',
   default: false,
});

export const systemRoles = atom<Role[]>({
   key: 'systemRoles',
   default: [] as Role[],
});

export const showManageMembersState = atom<{
   show: boolean;
   insuranceId: string;
} | null>({
   key: 'showManageMembersState',
   default: null,
});

export const showAddInsuranceUserState = atom<{
   show: boolean;
   insuranceId: string;
} | null>({
   key: 'showAddInsuranceUserState',
   default: null,
});

export const showCreateRoleState = atom({
   key: 'showCreateRoleState',
   default: false,
});
export const useSideBarFilter = atom<{
   useFilter: boolean;
   filterParam: string;
} | null>({
   key: 'useSideBarFilter',
   default: null,
});
export const patientCaseId = atom<string>({
   key: 'patientCaseId',
   default: '',
});

export const showApproveRequestState = atom<{
   show: boolean;
   requestId: string;
   action: 'approve' | 'reject';
} | null>({
   key: 'showApproveRequestState',
   default: null,
});

export const showAddUserState = atom({
   key: 'showAddUserState',
   default: false,
});

export const showEditRoleState = atom<{
   show: boolean;
   roleId: string;
} | null>({
   key: 'showEditRoleState',
   default: null,
});

export const showManageUserState = atom<{
   show: boolean;
   user: IUser;
} | null>({
   key: 'showManageUserState',
   default: null,
});

export const showAddVisitToCaseState = atom<{
   show: boolean;
   caseId: string;
} | null>({
   key: 'showAddVisitToCaseState',
   default: null,
});
export const showMedicalActBillId = atom<string>({
   key: 'medicalActBillId',
   default: '',
});

export const showAddLocationLevelState = atom({
   key: 'showAddLocationLevelState',
   default: false,
});

export const showAddLocationState = atom({
   key: 'showAddLocationState',
   default: false,
});

export const showDeleteLocationState = atom<{
   show: boolean;
   locationId: number;
} | null>({
   key: 'showDeleteLocationState',
   default: null,
});

export const showDeleteLocationLevelState = atom<{
   show: boolean;
   locationLevelId: number;
} | null>({
   key: 'showDeleteLocationLevelState',
   default: null,
});

export const showEditLocationState = atom<{
   show: boolean;
   locationId: number;
} | null>({
   key: 'showEditLocationState',
   default: null,
});

export const showEditLocationLevelState = atom<{
   show: boolean;
   locationLevelId: number;
} | null>({
   key: 'showEditLocationLevelState',
   default: null,
});
export const showConsumableBillId = atom<string>({
   key: 'medicalConsumableBillId',
   default: '',
});
export const showDrugBillId = atom<string>({
   key: 'medicalDrugBillId',
   default: '',
});

export const policyId = atom<string | null>({
   key: 'policyIdState',
   default: null,
});
export const insuranceMemberToEdit = atom<any | null>({
   key: 'insuranceMemberToEdit',
   default: null,
});

export const viewHistoryResponseState = atom<{
   show: boolean;
   response: any;
} | null>({
   key: 'ViewHistoryResponseState',
   default: null,
});

export const activeEligibilityTabState = atom<'check' | 'result'>({
   key: 'activeEligibilityTabState',
   default: 'check',
});

export const eligibilityCheckResultState = atom<any | null>({
   key: 'eligibilityCheckResultState',
   default: null,
});

export const showDeleteInsuranceState = atom<{ show: boolean; insuranceId: string } | null>({
   key: 'showDeleteInsuranceState',
   default: null,
});

export const showApprovalRequestAuditLogsState = atom<{
   show: boolean;
   requestId: string;
} | null>({
   key: 'ShowApprovalRequestAuditLogsState',
   default: null,
});

export const showAddOrEditMedicalItemState = atom<{
   show: boolean;
   itemType: EMedicalItemType;
   action: 'add' | 'edit';
   item?: IMedicalItem;
} | null>({
   key: 'ShowAddOrEditMedicalItem',
   default: null,
});

export const showDeleteMedicalItemState = atom<{
   show: boolean;
   itemId: string;
   itemType: EMedicalItemType;
} | null>({
   key: 'ShowDeleteMedicalItem',
   default: null,
});

export const showAddOrEditTariffState = atom<{
   show: boolean;
   action: 'add' | 'edit';
   itemType: EMedicalItemType;
   tariff?: ITariff;
} | null>({
   key: 'ShowAddOrEditTariff',
   default: null,
});

export const showDeleteTariffState = atom<{
   show: boolean;
   tariffId: string;
   itemType: EMedicalItemType;
} | null>({
   key: 'ShowDeleteTariff',
   default: null,
});

export const showRequestApprovalState = atom<{
   show: boolean;
   item: IMedicalItem;
   patient: IPatient;
   insurance: IInsurance;
} | null>({
   key: 'ShowRequestApproval',
   default: null,
});

export const showViewApprovalRequestState = atom<{
   show: boolean;
   request: IApprovalRequest;
} | null>({
   key: 'ShowViewApprovalRequest',
   default: null,
});

export const showAddOrEditRoleState = atom<{
   show: boolean;
   action: 'add' | 'edit';
   role?: Role;
} | null>({
   key: 'ShowAddOrEditRole',
   default: null,
});

export const showDeleteRoleState = atom<{
   show: boolean;
   role: Role;
} | null>({
   key: 'ShowDeleteRole',
   default: null,
});

export const showAddOrEditPractitionerState = atom<{
   show: boolean;
   action: 'add' | 'edit';
   practitioner?: IRMPractitioner;
} | null>({
   key: 'ShowAddOrEditPractitioner',
   default: null,
});

export const showDeletePractitionerState = atom<{
   show: boolean;
   practitionerId: string;
} | null>({
   key: 'ShowDeletePractitioner',
   default: null,
});

export const showAddOrEditFacilityState = atom<{
   show: boolean;
   action: 'add' | 'edit';
   facility?: IRMFacility;
} | null>({
   key: 'ShowAddOrEditFacility',
   default: null,
});

export const showDeleteFacilityState = atom<{
   show: boolean;
   facilityId: string;
} | null>({
   key: 'ShowDeleteFacility',
   default: null,
});

export const showAddOrEditVoucherState = atom<{
   show: boolean;
   action?: 'add' | 'edit';
   voucher?: IVoucher;
} | null>({
   key: 'ShowAddOrEditVoucher',
   default: null,
});

export const showDeleteVoucherState = atom<{
   show: boolean;
   voucherId: string;
} | null>({
   key: 'ShowDeleteVoucher',
   default: null,
});

export const showCreateTokenState = atom<boolean>({
   key: 'showCreateTokenState',
   default: false,
});

export const showViewTokenState = atom<{
   show: boolean;
   token: IToken;
} | null>({
   key: 'showViewTokenState',
   default: null,
});

export const showRegenerateTokenState = atom<{
   show: boolean;
   token: IToken;
} | null>({
   key: 'showRegenerateTokenState',
   default: null,
});

export const showUpdateTokenOriginState = atom<{
   show: boolean;
   token: IToken;
} | null>({
   key: 'showUpdateTokenOriginState',
   default: null,
});

export const showDeactivateTokenState = atom<{
   show: boolean;
   token: IToken;
} | null>({
   key: 'showDeactivateTokenState',
   default: null,
});

export const showActivateTokenState = atom<{
   show: boolean;
   token: IToken;
} | null>({
   key: 'showActivateTokenState',
   default: null,
});
